import React, { useEffect, useState } from "react";
import { ShieldCheck, CheckCircle2, XCircle, Wallet } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export default function PurchaseApprovals() {
  const [reqs, setReqs] = useState([]);
  const [children, setChildren] = useState([]);
  const [limits, setLimits] = useState({});
  const load = async () => {
    try {
      const [r, c] = await Promise.all([
        api.get("/parent/purchase-requests"),
        api.get("/parent/children"),
      ]);
      setReqs(r.data || []);
      setChildren(c.data || []);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const decide = async (id, action) => {
    try {
      await api.post(`/parent/purchase-requests/${id}/${action}`);
      toast.success(action === "approve" ? "Purchase approved — pack unlocked for your child 🎉" : "Purchase declined");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const saveLimit = async (childId) => {
    try {
      const val = parseFloat(limits[childId]);
      await api.post(`/parent/children/${childId}/spend-limit`, { monthly_limit: isNaN(val) ? 0 : val });
      toast.success("Spending limit updated.");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (children.length === 0 && reqs.length === 0) return null;

  return (
    <div className="rounded-2xl border border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/5 p-5 mb-6" data-testid="purchase-approvals">
      <h2 className="font-bold mb-3 flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[hsl(var(--primary))]" /> Purchases & spending limits</h2>

      {children.length > 0 && (
        <div className="mb-3" data-testid="spend-limits">
          <p className="text-xs text-[hsl(var(--muted-foreground))] mb-2">Set a monthly spending limit for each child. Set 0 for no limit (purchases still need your approval).</p>
          {children.map((c) => (
            <div key={c.id} className="flex flex-wrap items-center gap-2 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-3 mb-2" data-testid={`child-limit-${c.id}`}>
              <Wallet className="w-4 h-4 text-[hsl(var(--muted-foreground))]" />
              <span className="text-sm font-semibold">{c.name}</span>
              <span className="text-xs text-[hsl(var(--muted-foreground))]">spent ${Number(c.spent_total || 0).toFixed(2)} · limit {Number(c.spend_limit) > 0 ? `$${Number(c.spend_limit).toFixed(2)}` : "none"}</span>
              <div className="flex items-center gap-1 ml-auto">
                <Input type="number" min="0" step="1" placeholder="Set limit $" defaultValue={c.spend_limit || ""} onChange={(e) => setLimits({ ...limits, [c.id]: e.target.value })} className="w-28 h-8" data-testid={`limit-input-${c.id}`} />
                <Button size="sm" variant="outline" onClick={() => saveLimit(c.id)} data-testid={`limit-save-${c.id}`}>Save</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {reqs.length > 0 && (
        <div className="space-y-2" data-testid="purchase-requests-list">
          <div className="text-sm font-semibold mb-1">Pending purchase requests ({reqs.length})</div>
          {reqs.map((r) => (
            <div key={r.id} className="flex items-center gap-3 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-3" data-testid={`purchase-req-${r.id}`}>
              <span className="text-2xl shrink-0">{r.cover_emoji || "🎮"}</span>
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{r.pack_title} <span className="text-[hsl(var(--muted-foreground))]">· ${r.price}</span></div>
                <div className="text-xs text-[hsl(var(--muted-foreground))] truncate">{r.child_name} wants to unlock "{r.game_title}"</div>
              </div>
              <Button size="sm" className="rounded-full bg-emerald-500 hover:bg-emerald-600 shrink-0" onClick={() => decide(r.id, "approve")} data-testid={`purchase-approve-${r.id}`}><CheckCircle2 className="w-4 h-4" /> Approve</Button>
              <Button size="sm" variant="outline" className="shrink-0" onClick={() => decide(r.id, "reject")} data-testid={`purchase-reject-${r.id}`}><XCircle className="w-4 h-4" /> Decline</Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
